import { captureException } from '@sentry/react';

import Cache from '@townsquare/cache';
import { getConfig } from '@townsquare/config';
import { RequestError } from '@townsquare/error-state/classes';
import { localStorageFacade } from '@townsquare/facade';
import { RestClient } from '@townsquare/rest-client';

const ONE_HOUR = 1000 * 60 * 60;
const ONE_MONTH = ONE_HOUR * 24 * 30;

const HaveISeenItFlags = ['NO_WORKSPACE_BENEFITS_DIALOG'] as const;

type HaveISeenItFlag = (typeof HaveISeenItFlags)[number];

type FlagResponse = {
  status: boolean;
  lastSeenDate: Date | null;
};

const haveISeenItCache = new Cache();

const toKey = (flagKey: HaveISeenItFlag) => `townsquare_have_i_seen_it_${flagKey}`;

class HaveISeenItClient extends RestClient {
  constructor() {
    super({
      baseUrl: getConfig().haveISeenItUrl,
    });
    // Populate the cache from localStorage
    HaveISeenItFlags.forEach(flag => {
      if (localStorageFacade.getItem(`cache_${flag}`) === 'true') {
        haveISeenItCache.set(flag, true, ONE_MONTH);
      }
    });
  }

  private setCache(flagKey: HaveISeenItFlag, value: boolean) {
    haveISeenItCache.set(flagKey, value, value ? ONE_MONTH : ONE_HOUR);
    localStorageFacade.setItem(`cache_${flagKey}`, value.toString());
  }

  async setHasSeenFlag(flagKey: HaveISeenItFlag) {
    this.setCache(flagKey, true);
    try {
      return this.postResource('', {
        flagKey: toKey(flagKey),
      });
    } catch (error) {
      captureException(error, {
        extra: { flagKey },
      });
    }
  }

  async hasSeenFlag(flagKey: HaveISeenItFlag) {
    if (haveISeenItCache.has(flagKey)) {
      return Promise.resolve(haveISeenItCache.get(flagKey));
    }
    try {
      const flagResponse = await this.getResource<FlagResponse>(`?flagKey=${toKey(flagKey)}`);
      this.setCache(flagKey, flagResponse.status);
      return flagResponse.status;
    } catch (error) {
      if (!(error instanceof RequestError) || error.statusCode !== 401) {
        captureException(error, {
          extra: {
            flagKey,
            statusCode: error instanceof RequestError ? error.statusCode : 'N/A',
          },
        });
      }
      // If we get an error, we should assume the user has not seen the flag.
      this.setCache(flagKey, false);
      return false;
    }
  }
}

/**
 * @deprecated - Soon this will only be available to services inside the Engagement Platform.
 * We need a solution for non-Atlas users in Status.
 *
 * We should prefer the `Onboarding` component,
 * that uses Status to determine if the user has seen the onboarding. This client can be used
 * when the user is NOT a Atlas/Status/Watermelon user.
 */
export const haveISeenItClient = new HaveISeenItClient();
